import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import { HelmetProvider } from "@dr.pogodin/react-helmet";
import { devLog } from "@/utils/devLog";

// Show an "Updating" overlay instead of a blank reload loop
function showUpdateOverlay() {
  const existing = document.getElementById('sw-update-overlay');
  if (existing) return; // already showing
  
  const overlay = document.createElement('div');
  overlay.id = 'sw-update-overlay';
  overlay.style.cssText = `
    position: fixed; inset: 0; z-index: 99999;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    background: #1a1a2e; color: white; font-family: system-ui, sans-serif;
  `;
  overlay.innerHTML = `
    <div style="text-align:center; padding: 2rem;">
      <img src="/icon-pixel-192.png" alt="ClearCommunicationApp"
        width="96" height="96"
        style="margin: 0 auto 1.5rem; image-rendering: pixelated; border-radius: 16px; box-shadow: 0 8px 30px rgba(0,229,204,0.35); background: white; padding: 6px;" />
      <p style="font-size: 1.25rem; font-weight: 600; margin-bottom: 0.5rem; color: #00E5CC;">
        Updating ClearCommunicationApp
      </p>
      <p style="font-size: 0.875rem; color: #a0a0b8; margin-bottom: 1.5rem;">
        Hang tight! A new version is loading...
      </p>
      <div style="display:flex; gap:6px; justify-content:center;">
        <div style="width:8px;height:8px;border-radius:50%;background:#00E5CC;animation:bounce 1.4s infinite ease-in-out both;animation-delay:0s;"></div>
        <div style="width:8px;height:8px;border-radius:50%;background:#00E5CC;animation:bounce 1.4s infinite ease-in-out both;animation-delay:0.16s;"></div>
        <div style="width:8px;height:8px;border-radius:50%;background:#00E5CC;animation:bounce 1.4s infinite ease-in-out both;animation-delay:0.32s;"></div>
      </div>
      <style>
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
      </style>
    </div>
  `;
  document.body.appendChild(overlay);
}

// Register service worker for PWA functionality
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        devLog('SW registered:', registration.scope);

        // Check for updates every 60 seconds and on page focus
        setInterval(() => registration.update(), 60000);
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') registration.update();
        });

        // Defer applying a new SW until the user is NOT actively using the app
        // (page hidden). This prevents random mid-session reloads that can
        // race with long-running requests like ElevenLabs audio generation
        // and trigger spurious logouts.
        let pendingWorker = null;
        const tryApplyPending = () => {
          if (pendingWorker && document.visibilityState === 'hidden') {
            showUpdateOverlay();
            pendingWorker.postMessage({ type: 'SKIP_WAITING' });
            pendingWorker = null;
          }
        };

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                pendingWorker = newWorker;
                // Only apply when the page is hidden / user is not interacting
                tryApplyPending();
              }
            });
          }
        });

        document.addEventListener('visibilitychange', tryApplyPending);
      })
      .catch((error) => {
        devLog('SW registration failed:', error);
      });

    // When the new SW takes control, reload ONCE using sessionStorage guard
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      const reloadKey = 'sw-reloaded';
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, 'true');
        showUpdateOverlay();
        setTimeout(() => window.location.reload(), 800);
      } else {
        // Already reloaded — remove any leftover overlay
        const overlay = document.getElementById('sw-update-overlay');
        if (overlay) overlay.remove();
      }
    });
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>,
);
